import { FormDatepickerField, FormSelect } from 'components/forms';
import { useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useSubmitNew from 'shared/hooks/useSubmitNew';
import MassActionModalLayout from 'shared/layouts/massActionModalLayout';
import { MassActionItemProps, UseMassActionProps } from 'shared/types';
import { oneYearAheadToCurrentDate } from 'shared/utility';
import { MassActionForm } from 'types/backend';

type ChangeDeviationDateActionForm = { endDate: string; comments: string };
export type ChangeDeviationDateForm = MassActionForm<ChangeDeviationDateActionForm>;

type ChangeDeviationDateProps<T> = MassActionItemProps<ChangeDeviationDateForm, T, ChangeDeviationDateActionForm>;

enum ReasonEnum {
    EXTEND = 'Extend',
    EXPIRE = 'Expire',
}

const reasonOptions = [
    {
        value: ReasonEnum.EXTEND,
        displayText: ReasonEnum.EXTEND,
        original: ReasonEnum.EXTEND,
    },
    {
        value: ReasonEnum.EXPIRE,
        displayText: ReasonEnum.EXPIRE,
        original: ReasonEnum.EXPIRE,
    },
];

export default function ChangeDeviationDate<T>({
    defaultValues,
    onSubmit,
    isLoading,
    tableDataContext,
    rowCount,
    columnConfig,
    allRowsSelected,
    validate,
}: ChangeDeviationDateProps<T>) {
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm<ChangeDeviationDateForm>({
        defaultValues,
    });
    const reason = watch('actionForm.comments');

    const setEndDate = useCallback(
        (reason) => {
            const now = new Date().toLocaleDateString();
            if (reason === ReasonEnum.EXTEND) {
                setValue('actionForm.endDate', oneYearAheadToCurrentDate());
            }
            if (reason === ReasonEnum.EXPIRE) {
                setValue('actionForm.endDate', now);
            }
        },
        [setValue]
    );

    useEffect(() => {
        setEndDate(reason);
    }, [reason, setEndDate]);

    return (
        <MassActionModalLayout
            isLoading={isLoading}
            tableDataContext={tableDataContext}
            columnConfig={columnConfig}
            allRowsSelected={allRowsSelected}
            rowCount={rowCount}
            submitHandler={handleSubmit(onSubmit)}
        >
            <FormSelect
                name={'actionForm.comments'}
                label='Reason'
                control={control}
                options={reasonOptions}
                rules={{
                    required: 'Reason is required',
                }}
                fieldError={errors?.['actionForm']?.['comments']}
            />
            <FormDatepickerField name={'actionForm.endDate'} label='End Date' control={control} disabled />
        </MassActionModalLayout>
    );
}

export function useChangeDeviationDateMassAction<T>({
    controllerName,
    affectedDataContext,
    selected,
    allRowsSelected,
    onSuccess,
}: UseMassActionProps<T>) {
    const { filtersApplied } = useContext(affectedDataContext);

    const changeDeviationDateMapper = (form: ChangeDeviationDateForm) => ({
        ...form,
        filters: filtersApplied,
        selected,
        allRowsSelected,
        type: 'Change deviation end date',
    });

    return useSubmitNew({
        name: `${controllerName}/massaction/changedeviationdate`,
        mapper: changeDeviationDateMapper,
        context: affectedDataContext,
        onSuccess: onSuccess,
    });
}
